<template>
  <div
    class="flex w-full items-start gap-3 rounded-lg border border-blue-100 bg-refonte-pale-violet-2 p-4 text-left text-sm">
    <div class="w-full">
      <div class="mb-0.5 w-full font-bold tracking-wide text-blue-600">
        <template v-if="props.signature_unique">
          Vérifiez vos informations
        </template>
        <template v-else> Signataire sélectionné : </template>
      </div>
      <div class="font-bold">
        {{ props.selected_signataire.nomPrenom }}
      </div>
      <div>
        {{ props.selected_signataire.email }}
      </div>
      <div class="italic">
        {{
          props.selected_signataire.telephone &&
          props.selected_signataire.telephone.length > 0
            ? props.selected_signataire.telephone
            : 'Numéro de téléphone non renseigné'
        }}
      </div>
      <div
        class="text-xs font-bold italic text-jinius-red"
        v-if="!props.signataire_phone_valid">
        <template v-if="signature_unique">
          Un numéro de téléphone mobile est requis pour recevoir votre code SMS
          de validation. Merci de le rajouter ou de prendre contact avec le
          signataire.
        </template>
        <template v-else>
          Le numéro renseigné n'est pas un numéro de téléphone mobile
          international valide (ex. : +33 6 12 34 56 78). Merci de le rajouter
          ou de prendre contact avec le signataire.
        </template>
      </div>
      <hr class="mx-auto my-4 w-full border-core-300" />
      <div
        class="mb-2 flex items-center gap-2 text-xs"
        v-if="props.has_admin_rights">
        <span class="info-icon shrink-0 text-blue-600">
          <IconModify />
        </span>
        <template v-if="props.signature_unique">
          <div>
            Pour modifier votre numéro de téléphone, rendez-vous sur la page
            <span
              class="font-bold text-blue-600 hover:underline"
              v-on:click="goToInformationPersonnelles"
              >Mes informations personnelles.</span
            >
            <br />
            Pour corriger votre nom, prénom, ou email, rendez-vous sur la page
            <span
              class="font-bold text-blue-600 hover:underline"
              v-on:click="goToAccount"
              >Mon compte.</span
            >
          </div>
        </template>
        <template v-else>
          <div>
            Le numéro de téléphone est incorrect ?
            <br />
            Vous pouvez le modifier depuis l'écran
            <span
              class="font-bold text-blue-600 hover:underline"
              v-on:click="goToInterlocuteursList"
              >Interlocuteurs.</span
            >
          </div>
        </template>
      </div>
      <div
        class="mb-2 flex items-center gap-2 text-xs"
        v-else>
        <span class="info-icon shrink-0 text-blue-600">
          <IconModify />
        </span>
        <div>
          Ces informations semblent incorrectes?
          <br />
          <span>
            Veuillez vous rapprocher de
            <span class="font-bold">l'administrateur du compte</span>
            pour les faire modifier.
          </span>
        </div>
      </div>
      <div
        class="mb-2 flex items-center gap-2 text-xs"
        v-if="
          props.open_signature_manuelle &&
          props.signature_unique &&
          showSignatureManuelle
        ">
        <span class="info-icon shrink-0 text-blue-600">
          <IconSignedV1 />
        </span>
        <p class="text-xs">
          <button
            class="hover:underline"
            v-on:click="openSignatureManuelleConfirm">
            Confirmation par téléphone portable impossible
          </button>
        </p>
      </div>
    </div>
  </div>
  <signature-manuelle-confirm-modal
    v-if="props.open_signature_manuelle"
    v-bind:document="props.document_name"
    v-bind:on_ok="props.open_signature_manuelle"
    v-bind:show="showSignatureManuelleConfirmModal"
    v-bind:on_close="closeSignatureManuelleConfirm" />
</template>

<script setup lang="ts">
  import { InterlocuteurSignataire } from '@/types/gestion/interlocuteurs-types';
  import IconModify from '@/assets/img/refonte/icons/action/icon-modify.svg?component';
  import IconSignedV1 from '@/assets/img/refonte/icons/display/icon-signed-v1.svg?component';
  import { computed, ref } from 'vue';
  import SignatureManuelleConfirmModal from '@/components/refonte/modals/signataire/signature-manuelle-confirm-modal.vue';
  const props = withDefaults(
    defineProps<{
      signature_unique: boolean;
      selected_signataire: InterlocuteurSignataire;
      class?: string;
      has_admin_rights: boolean;
      signataire_phone_valid: boolean;
      client_id?: number;
      open_signature_manuelle?: Function;
      document_name: string;
    }>(),
    {
      class: ''
    }
  );
  const showSignatureManuelleConfirmModal = ref<boolean>(false);
  const openSignatureManuelleConfirm = () => {
    showSignatureManuelleConfirmModal.value = true;
  };
  const closeSignatureManuelleConfirm = () => {
    showSignatureManuelleConfirmModal.value = false;
  };

  const goToInterlocuteursList = () => {
    window.open(
      `/gestion/interlocuteurs?clientIds=${props.client_id}`,
      '_blank'
    );
  };

  const goToInformationPersonnelles = () => {
    window.open('/account/informations#telephone-mobile', '_blank');
  };

  const goToAccount = () => {
    window.open(`${import.meta.env.VITE_API_AUTH}/account/redirect`, '_blank');
  };

  const showSignatureManuelle = computed(
    () => !props.selected_signataire.telephone
  );
</script>

<style lang="scss" scoped>
  .info-icon {
    @apply mr-2 flex shrink-0 items-center justify-center;
    width: 16px;
    height: 16px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 16px;
      height: 16px;
    }
  }
  .info-add-participant-icon {
    @apply flex shrink-0 items-center justify-center;
    width: 14px;
    height: 14px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 14px;
      height: 14px;
    }
  }

  .description-card {
    @apply bg-refonte_blue-100 text-refonte_blue-600;
    .description-card-title {
      font-weight: 600;
      font-size: 14px;
    }
  }

  .selected-card {
    @apply bg-refonte-pale-violet-2 text-refonte-black;
    gap: 6px;
    line-height: 20px;

    .selected-card-title {
      font-weight: 600;
      font-size: 14px;
    }
  }

  .warning-card {
    @apply flex border-amber-100 bg-amber-50;
    .warning-card-row {
      @apply flex items-center;
      gap: 6px;
    }
  }
  .icon-phone {
    @apply flex shrink-0 items-center justify-center;
    width: 14px;
    height: 14px;

    :deep(svg) {
      @apply text-refonte_blue-600;
      width: 14px;
      height: 14px;
    }
  }
</style>
